/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    excludeKanbanItems,
    excludeWhatsappApproached,
    conversationScope,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        exclude_kanban_items: excludeKanbanItems,
        exclude_whatsapp_approached: excludeWhatsappApproached,
        conversation_scope: conversationScope,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
        conversation_scope: payload.conversationScope,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    conversationScope,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        conversation_scope: conversationScope,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getAttributes(conversationId) {
    return axios.get(`${this.url}/${conversationId}/custom_attributes`);
  }

  scheduleMessage({ conversation_id, inbox_id, content, scheduled_at, title, is_recurrent, period }) {
    return axios.post(`${this.url}/${conversation_id}/scheduled_messages`, {
      scheduled_message: {
        conversation_id,
        inbox_id,
        content,
        scheduled_at,
        title,
        status: 'pending',
        is_recurrent,
        period,
      },
    });
  }

  getScheduledMessagesCount(conversationId) {
    return axios.get(`${this.url}/${conversationId}/scheduled_messages/count`);
  }

  getScheduledMessages(conversationId) {
    return axios.get(`${this.url}/${conversationId}/scheduled_messages`);
  }

  deleteScheduledMessage(conversationId, messageId) {
    return axios.delete(`${this.url}/${conversationId}/scheduled_messages/${messageId}`);
  }

  updateScheduledMessage(conversationId, messageId, data) {
    return axios.patch(`${this.url}/${conversationId}/scheduled_messages/${messageId}`, {
      scheduled_message: {
        inbox_id: data.inbox_id,
        content: data.content,
        scheduled_at: data.scheduled_at,
        title: data.title,
        is_recurrent: data.is_recurrent,
        period: data.period,
      },
    });
  }

  requestCopilot(conversationId, body) {
    return axios.post(`${this.url}/${conversationId}/copilot`, body);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  getPersonalExclusion(conversationId) {
    return axios.get(`${this.url}/${conversationId}/personal_exclusion`);
  }

  createPersonalExclusion(conversationId, { confirmationPhone, reason }) {
    return axios.post(`${this.url}/${conversationId}/personal_exclusion`, {
      confirmation_phone: confirmationPhone,
      reason,
    });
  }

  deletePersonalExclusion(conversationId) {
    return axios.delete(`${this.url}/${conversationId}/personal_exclusion`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
